/**
 * App-readiness signal for the server-injected crawlable block.
 *
 * `backend/utils/seoRenderer.js` injects a clipped `#seo-prerender` block outside
 * `#root` so crawlers that don't execute JS — and renderers that snapshot the DOM
 * after load — still get real content. That block has to be retired once the SPA
 * has painted, or a duplicate of the page sits in the DOM.
 *
 * Deciding *when* is the delicate part. The teardown used to infer readiness from
 * how much text `#root` contained, but a content-shaped skeleton clears that
 * threshold with nothing real on screen, which made the crawlable block disappear
 * from a page that was still visually empty. So screens assert readiness instead
 * of the script guessing at it.
 *
 * Contract:
 *  - Call `markAppReady()` from a screen once it has committed real content.
 *  - Never call it from a skeleton, placeholder, or Suspense fallback.
 *  - Not calling it is safe: the block falls through to the 4s HARD_MS backstop,
 *    which is the behaviour the page had before. Calling it too early is not.
 */

let marked = false;

export function markAppReady() {
  if (marked) return;
  if (typeof document === 'undefined') return;
  marked = true;
  document.documentElement.setAttribute('data-app-ready', '1');
}

/** Test seam — lets a suite reset the module-level latch between cases. */
export function resetAppReadyForTests() {
  marked = false;
  if (typeof document !== 'undefined') {
    document.documentElement.removeAttribute('data-app-ready');
  }
}

export default markAppReady;
